import React, { useState, useEffect } from 'react';
import { agentsService } from '../../services';
import agencyService from '../../services/agencyService';
import './SystemLogs.css';

const SystemLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    level: '',
    component: '',
    agencyId: '',
    startDate: '',
    endDate: '',
    correlationId: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    components: [],
    agencies: [],
    levels: ['error', 'warn', 'info', 'debug']
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadFilterOptions();
    loadStats();
    loadLogs();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filters, pagination.page]);

  const loadFilterOptions = async () => {
    try {
      const response = await agencyService.request('/admin/logs/filters');
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await agencyService.request('/admin/logs/stats');
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load log statistics:', error);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await agencyService.request(`/admin/logs?${queryParams}`);
      if (response.success) {
        setLogs(response.data.logs);
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination
        }));
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      level: '',
      component: '',
      agencyId: '',
      startDate: '',
      endDate: '',
      correlationId: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportLogs = async () => {
    try {
      const queryParams = new URLSearchParams(
        Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      );

      const response = await fetch(`/api/admin/logs/export?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `system_logs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to export logs:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'error': return '#dc3545';
      case 'warn': return '#ffc107';
      case 'info': return '#17a2b8';
      case 'debug': return '#6c757d';
      default: return '#000';
    }
  };

  const LogDetailsModal = ({ log, onClose }) => {
    if (!log) return null;

    return (
      <div className="log-modal-overlay" onClick={onClose}>
        <div className="log-modal-content" onClick={e => e.stopPropagation()}>
          <div className="log-modal-header">
            <h3>Log Details</h3>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>
          <div className="log-modal-body">
            <div className="log-detail-grid">
              <div className="log-detail-item">
                <strong>Timestamp:</strong>
                <span>{formatTimestamp(log.timestamp)}</span>
              </div>
              <div className="log-detail-item">
                <strong>Level:</strong>
                <span className="log-level" style={{ color: getLevelColor(log.level) }}>
                  {log.level.toUpperCase()}
                </span>
              </div>
              <div className="log-detail-item">
                <strong>Component:</strong>
                <span>{log.component}</span>
              </div>
              <div className="log-detail-item">
                <strong>Action:</strong>
                <span>{log.action}</span>
              </div>
              <div className="log-detail-item">
                <strong>Message:</strong>
                <span>{log.message}</span>
              </div>
              {log.user_email && (
                <div className="log-detail-item">
                  <strong>User:</strong>
                  <span>{log.user_email}</span>
                </div>
              )}
              {log.correlation_id && (
                <div className="log-detail-item">
                  <strong>Correlation ID:</strong>
                  <span className="correlation-id">{log.correlation_id}</span>
                </div>
              )}
              {log.request_url && (
                <div className="log-detail-item">
                  <strong>Request URL:</strong>
                  <span>{log.request_url}</span>
                </div>
              )}
              {log.stack_trace && (
                <div className="log-detail-item full-width">
                  <strong>Stack Trace:</strong>
                  <pre className="stack-trace">{log.stack_trace}</pre>
                </div>
              )}
              {log.business_context && (
                <div className="log-detail-item full-width">
                  <strong>Business Context:</strong>
                  <pre className="context-json">
                    {JSON.stringify(JSON.parse(log.business_context), null, 2)}
                  </pre>
                </div>
              )}
              {log.technical_context && (
                <div className="log-detail-item full-width">
                  <strong>Technical Context:</strong>
                  <pre className="context-json">
                    {JSON.stringify(JSON.parse(log.technical_context), null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="system-logs-container">
      <div className="logs-header">
        <h2>System Logs</h2>
        <div className="logs-actions">
          <button onClick={exportLogs} className="export-btn">
            Export CSV
          </button>
          <button onClick={loadLogs} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      {stats.stats && (
        <div className="logs-stats">
          <div className="stat-card">
            <h4>Errors (24h)</h4>
            <span className="stat-number error">{stats.stats.errors_24h}</span>
          </div>
          <div className="stat-card">
            <h4>Warnings (24h)</h4>
            <span className="stat-number warning">{stats.stats.warnings_24h}</span>
          </div>
          <div className="stat-card">
            <h4>Info (24h)</h4>
            <span className="stat-number info">{stats.stats.info_24h}</span>
          </div>
          <div className="stat-card">
            <h4>Total (24h)</h4>
            <span className="stat-number total">{stats.stats.total_24h}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="logs-filters">
        <div className="filter-row">
          <select
            value={filters.level}
            onChange={e => handleFilterChange('level', e.target.value)}
          >
            <option value="">All Levels</option>
            {filterOptions.levels.map(level => (
              <option key={level} value={level}>{level.toUpperCase()}</option>
            ))}
          </select>

          <select
            value={filters.component}
            onChange={e => handleFilterChange('component', e.target.value)}
          >
            <option value="">All Components</option>
            {filterOptions.components.map(component => (
              <option key={component} value={component}>{component}</option>
            ))}
          </select>

          <select
            value={filters.agencyId}
            onChange={e => handleFilterChange('agencyId', e.target.value)}
          >
            <option value="">All Agencies</option>
            {filterOptions.agencies.map(agency => (
              <option key={agency.id} value={agency.id}>{agency.name}</option>
            ))}
          </select>

          <input
            type="datetime-local"
            placeholder="Start Date"
            value={filters.startDate}
            onChange={e => handleFilterChange('startDate', e.target.value)}
          />

          <input
            type="datetime-local"
            placeholder="End Date"
            value={filters.endDate}
            onChange={e => handleFilterChange('endDate', e.target.value)}
          />

          <input
            type="text"
            placeholder="Correlation ID"
            value={filters.correlationId}
            onChange={e => handleFilterChange('correlationId', e.target.value)}
          />

          <button onClick={clearFilters} className="clear-filters-btn">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="logs-table-container">
        {loading ? (
          <div className="loading">Loading logs...</div>
        ) : (
          <table className="logs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Level</th>
                <th>Component</th>
                <th>Action</th>
                <th>Message</th>
                <th>User</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className={`log-row log-${log.level}`}>
                  <td>{formatTimestamp(log.timestamp)}</td>
                  <td>
                    <span 
                      className="log-level-badge"
                      style={{ backgroundColor: getLevelColor(log.level) }}
                    >
                      {log.level.toUpperCase()}
                    </span>
                  </td>
                  <td>{log.component}</td>
                  <td>{log.action}</td>
                  <td className="log-message">{log.message}</td>
                  <td>{log.user_email || '-'}</td>
                  <td>
                    <button 
                      onClick={() => setSelectedLog(log)}
                      className="view-details-btn"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="logs-pagination">
          <button 
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages} 
            ({pagination.total} total logs)
          </span>
          <button 
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.pages}
          >
            Next
          </button>
        </div>
      )}

      {/* Log Details Modal */}
      <LogDetailsModal 
        log={selectedLog} 
        onClose={() => setSelectedLog(null)} 
      />
    </div>
  );
};

export default SystemLogs;
